/**
 * ARC-60 — debt write guard (pure; no Firestore, no React).
 *
 * THE INCIDENT THIS EXISTS FOR
 * On 2026-08-18 a member's `debts` array in `financial_data/{uid}` was replaced
 * by the demo scaffold: "Credit Card A", "Student Loan B", "Car Loan C",
 * "Mortgage D" — the four rows that used to live in `getDefaultData().debts`.
 * Same defect class as ARC-14 (expenses), ARC-58 (income) and ARC-59 (risk).
 *
 * The mechanism was the load effect, and `debts` was seeded from that scaffold
 * in THREE places:
 *
 *   1. the failure branch                     — `setDebts(def.debts)`
 *   2. the empty-payload branch               — `setDebts(def.debts)`
 *   3. the loaded-document branch, per field  — `d.debts ?? getDefaultData().debts`
 *
 * Only (1) had `loadedOk.current = false` in front of it. (2) and (3) set
 * `loadedOk.current = true`, so autosave was armed with four invented debts
 * sitting in the same state `writeUserData` serialises, and fired ~2s later
 * with no member action at all. `debts` is a top-level field, so that write
 * REPLACED the member's real array wholesale.
 *
 * The rules here follow from that, and mirror ARC-58:
 *
 *   1. A read we do not trust produces no debt state and no write permission —
 *      `resolveDebtLoad`. A read that FAILED and a read whose shape we cannot
 *      PARSE collapse to one identical closed state, because both leave us
 *      equally ignorant of what the server holds.
 *   2. There is no branch anywhere in this module that returns a placeholder
 *      debt. `debts` is either the server's array or empty — the scaffold is
 *      gone from `getDefaultData()` entirely, so there is no default left for a
 *      future edit to reach for.
 *   3. `guardDebtWrite` is the last gate before the field enters a write, for
 *      the case a future change re-opens one of the paths above.
 *
 * Pure, so every failure path is testable without a Firestore.
 *
 * Non-goals (ARC-60): nothing here touches the payoff/amortisation maths, the
 * extra-payment simulator, or the Overview/Print consumers of the debt totals.
 */

import { stableStringify } from "../expenses/expenseWriteGuard";

export { stableStringify };

export const DEBT_LOAD_ERROR =
  "Could not load your debt data. Editing is disabled so nothing overwrites what is saved — please refresh the page.";

export const DEBT_OVERWRITE_BLOCKED =
  "Your saved debts were protected: placeholder entries were about to replace them. Nothing was overwritten.";

/* The names of the demo rows that were written over real member debts. Kept as
   NAMES ONLY, deliberately: this module must not contain a seedable scaffold,
   or removing it from getDefaultData would have achieved nothing. They exist
   here to be RECOGNISED and refused, never to be produced. */
export const PLACEHOLDER_DEBT_NAMES = [
  "Credit Card A",
  "Student Loan B",
  "Car Loan C",
  "Mortgage D",
];

const isPlainObject = (v) =>
  typeof v === "object" && v !== null && !Array.isArray(v);

/* Absent means "the member has saved no debts yet" — a normal, safe state.
   Anything else that is not an array is a value we do not understand, which is
   NOT the same thing and must not be treated as one. */
const isAbsent = (v) => v === undefined || v === null;

export const isUnparseableDebts = (v) => {
  if (isAbsent(v)) return false;
  /* A non-array, or an array holding anything that is not a debt record. The
     tab reads `debt.name.length` and `debt.current > 0` unguarded, so a rogue
     element is both a crash and — once coerced away — a silent deletion. */
  if (!Array.isArray(v)) return true;
  return v.some((entry) => !isPlainObject(entry));
};

const num = (v) => (typeof v === "number" && Number.isFinite(v) ? v : 0);

/**
 * Bring one stored debt record up to the current shape.
 *
 * `apr` / `monthlyPayment` / `totalInterestPaid` post-date the original schema,
 * and the derived `paidOff` / `monthlyInterest` are recomputed by the
 * dashboard's derive effect on every render pass. Doing it here as well means a
 * freshly loaded array already equals what that effect will produce, so the
 * committed snapshot matches the buffer and the tab does not open claiming
 * unsaved edits the member never made.
 */
export const normalizeDebt = (debt) => {
  const migrated = {
    ...debt,
    apr: debt.apr ?? 0,
    monthlyPayment: debt.monthlyPayment ?? 0,
    totalInterestPaid: debt.totalInterestPaid ?? 0,
  };
  const original = num(migrated.original);
  const current = num(migrated.current);
  return {
    ...migrated,
    paidOff: original ? ((original - current) / original) * 100 : 0,
    monthlyInterest: current * (num(migrated.apr) / 100 / 12),
  };
};

/**
 * Is this array the demo scaffold rather than member data?
 *
 * Name-based, and it requires MOST of the scaffold to be present: a member with
 * one debt they happened to call "Credit Card A" is not the incident. Callers
 * pair this with a check that the prior is not itself placeholder-named, so a
 * member who genuinely uses these names can still edit their own rows.
 */
export const isPlaceholderDebtSet = (v) => {
  if (!Array.isArray(v) || v.length === 0) return false;
  const names = v.map((d) => (isPlainObject(d) ? d.name : null));
  if (!names.every((n) => PLACEHOLDER_DEBT_NAMES.includes(n))) return false;
  return new Set(names).size >= 3;
};

/**
 * Has the member changed the debt buffer since the last server-confirmed state?
 * No committed snapshot means nothing has been confirmed yet — treat that as
 * "not dirty" so we never warn about edits the member did not make.
 */
export const hasUnsavedDebtChanges = (current, committed) => {
  if (!committed) return false;
  const shape = (s) => (Array.isArray(s?.debts) ? s.debts : []);
  return stableStringify(shape(current)) !== stableStringify(shape(committed));
};

/**
 * Decide the debt state a load result is allowed to produce.
 *
 * @param {{ok: boolean, data?: object|null}} result
 *   `ok` is false when the read threw or the service reported failure.
 *   `data` is the loaded document, or null/empty for a brand-new account.
 *
 * @returns {{debts: object[], committed: {debts: object[]}|null,
 *   canSave: boolean, error: string}}
 *   `committed` is the server-confirmed snapshot the tab's charts and summary
 *   render from; null means nothing is confirmed. `canSave: false` means the
 *   tab must be read-only — a failed read tells us nothing about what the
 *   server holds, so writing anything on top of it is guesswork.
 *
 * Note what this function cannot do: there is no branch that returns a
 * placeholder debt. `debts` is either the server's array or empty.
 */
export const resolveDebtLoad = ({ ok, data } = {}) => {
  /* One closed state, whatever the reason we do not trust the read. */
  const failedRead = () => ({
    debts: [],
    committed: null,
    canSave: false,
    error: DEBT_LOAD_ERROR,
  });

  if (!ok) return failedRead();

  /* A present-but-unreadable value is the quiet version of the same bug:
     coercing it to `[]` with saving left ENABLED means autosave writes an empty
     debt list over a server value we merely failed to parse, ~2s after load and
     with no user action. Ignoring a value we cannot read is fine; replacing it
     is the exact data loss this module exists to prevent. */
  if (isUnparseableDebts(data?.debts)) return failedRead();

  /* A document with no `debts` is either a brand-new account or one saved
     before the field existed. Both mean "the member has entered no debts", and
     an empty list is what that looks like — not four worked examples we then
     save on their behalf. */
  const debts = (Array.isArray(data?.debts) ? data.debts : []).map(
    normalizeDebt
  );

  return { debts, committed: { debts }, canSave: true, error: "" };
};

/**
 * Last gate before `debts` goes into a write.
 *
 * @param {{outgoing: object[]|undefined,
 *   committed: {debts: object[]}|null,
 *   server: object[]|undefined,
 *   outgoingTrusted: boolean}} params
 *   `server` is the document's current `debts`, read immediately before the
 *   write. `committed` is our own last server-confirmed snapshot.
 *   `outgoingTrusted` is false when the load that should have produced
 *   `outgoing` failed or could not be parsed.
 *
 * @returns {{debts: object[], blocked: boolean, warning: string}}
 *
 * The value is substituted rather than the whole save being refused. One save
 * carries every tab's state in a single document write, so refusing outright
 * would strand a member editing their expenses because of a tab they never
 * opened. Substituting keeps the write moving and still guarantees the array on
 * the server is never the one replaced.
 *
 * Deliberately NOT guarded against: an empty `outgoing` replacing a non-empty
 * prior. Deleting every debt is a real thing members do — `removeDebt` already
 * confirms it — and a guard that second-guessed it would make paying off your
 * last debt impossible to record.
 */
export const guardDebtWrite = ({
  outgoing,
  committed,
  server,
  outgoingTrusted = true,
} = {}) => {
  const allow = (value) => ({
    debts: Array.isArray(value) ? value : [],
    blocked: false,
    warning: "",
  });

  /* We could not read this field, so `outgoing` is not the member's debts — it
     is whatever the UI holds in their absence. Write the server's own value
     straight back, which makes the field a no-op for this save. Verbatim,
     including a value we failed to parse: we do not know it is safe to lose. */
  if (!outgoingTrusted) {
    return {
      debts: Array.isArray(server) ? server : [],
      blocked: false,
      warning: "",
    };
  }

  if (!isPlaceholderDebtSet(outgoing)) return allow(outgoing);

  /* Both copies are consulted. `server` is authoritative and catches a stale
     snapshot; `committed` covers the write where the pre-write read gave us
     nothing to compare against. A prior that is ITSELF placeholder-named means
     the member owns these names — leave them their edits. */
  const prior = [server, committed?.debts].find(
    (c) => Array.isArray(c) && c.length > 0 && !isPlaceholderDebtSet(c)
  );

  if (!prior) return allow(outgoing);

  return { debts: prior, blocked: true, warning: DEBT_OVERWRITE_BLOCKED };
};

export default resolveDebtLoad;
